import { catchError, distinctUntilChanged, filter, of, switchMap, tap, from } from 'rxjs';
import { ofType } from 'redux-observable';
import uiStateSlice from 'layout/redux/uiStateSlice';
import messageService from 'layout/services/messageService';
import { postRecommendedCommunities, getRecommendedCommunities$ } from '../apis/recommendedCommunities';
import {
    postCommunityBrochure,
    postPlanBrochure,
    postSpecBrochure,
    getBrochureDownloadUrl
} from '../apis/postBrochure';
import {
    postPlanBrochureBasic,
    postSpecBrochureBasic,
    postCommunityBrochureBasic,
    postBasicListingBrochureBasic,
} from '../apis/postBrochureBasic';
import brochureSlice from './slices';

const actions = uiStateSlice.actions;
const brochureActions = brochureSlice.actions;

export const postBuildablePlanBrochureEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postBuildeablePlanBrochure'),
    tap((action) => postCommunityBrochure(action.payload.parameters)),
    switchMap(() => of(actions.fetchModalFulfilled()))
  );

export const postCommunityBrochureEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postCommunityBrochure'),
    tap((action) => postCommunityBrochure(action.payload.parameters)),
    switchMap(() => of(actions.fetchModalFulfilled()))
  );

export const postPlanBrochureEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postPlanBrochure'),
    tap((action) => postPlanBrochure(action.payload.parameters)),
    switchMap(() => of(actions.fetchModalFulfilled()))
  );

export const postSpecBrochureEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postSpecBrochure'),
    tap((action) => postSpecBrochure(action.payload.parameters)),
    switchMap(() => of(actions.fetchModalFulfilled()))
  );

export const postRecommendedBrochureEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postRecommendedBrochure'),
    distinctUntilChanged(),
    tap((action) => postRecommendedCommunities(action.payload.parameters)),
    switchMap(() => of(actions.fetchModalFulfilled()))
  );

export const getRecommendedBrochureEpicPaid = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postSpecBrochure' ||
      action.payload.parameters.type === 'postPlanBrochure' ||
      action.payload.parameters.type === 'postCommunityBrochure' ||
      action.payload.parameters.type === 'postBuildeablePlanBrochure'),
    switchMap((action) => {
      if (action.payload.parameters.excludeRecommendations) {
        messageService.sendMessage({
          messageType: 'leadPosted', payload: {
            html: '',
          }
        });

        return of(actions.fetchModalFulfilled());
      }

      return getRecommendedCommunities$(action.payload.parameters).pipe(
        switchMap((data) => {
          messageService.sendMessage({
            messageType: 'leadPosted', isBasic: false, payload: {
              html: data,
            }
          });

          return of(actions.fetchModalFulfilled());
        }),
        catchError((err) => of(actions.fetchModalError(err)))
      );
    }),
  );


export const getRecommendedBrochureEpicBasic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postSpecBrochureBasic' ||
      action.payload.parameters.type === 'postPlanBrochureBasic' ||
      action.payload.parameters.type === 'postCommunityBrochureBasic' ||
      action.payload.parameters.type === 'postBasicListingBrochureBasic'),
    switchMap((action) => {
      if (action.payload.parameters.excludeRecommendations) {
        messageService.sendMessage({
          messageType: 'leadPosted', payload: {
            html: '',
          }
        });

        return of(actions.fetchModalFulfilled());
      }

      return getRecommendedCommunities$(action.payload.parameters).pipe(
        switchMap((data) => {
          messageService.sendMessage({
            messageType: 'leadPosted', isBasic: true, payload: {
              html: data,
            }
          });

          return of(actions.fetchModalFulfilled());
        }),
        catchError((err) => of(actions.fetchModalError(err)))
      );
    }),
  );

export const postSpecBrochureBasicEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postSpecBrochureBasic'),
    tap((action) => postSpecBrochureBasic(action.payload.parameters)),
    switchMap((action) => of(actions.fetchModalFulfilled(action)))
  );

export const postPlanBrochureBasicEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postPlanBrochureBasic'),
    tap((action) => postPlanBrochureBasic(action.payload.parameters)),
    switchMap((action) => of(actions.fetchModalFulfilled(action)))
  );

export const postCommunityBrochureBasicEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postCommunityBrochureBasic'),
    tap((action) => postCommunityBrochureBasic(action.payload.parameters)),
    switchMap((action) => of(actions.fetchModalFulfilled(action)))
  );

export const postBasicLisitingBrochureBasicEpic = (action$) =>
  action$.pipe(
    ofType(actions.fetchModal.type),
    filter((action) => action.payload.parameters.type === 'postBasicListingBrochureBasic'),
    tap((action) => postBasicListingBrochureBasic(action.payload.parameters)),
    switchMap((action) => of(actions.fetchModalFulfilled(action)))
  );

/**
* getBrochureDownloadUrlEpic: An Epic to handle the side effect of fetching a brochure download URL.
* This Epic listens for the `fetchBrochureDownloadUrl` action, makes an API call to fetch the URL,
* and dispatches either a success or failure action based on the result.
*
* @param {Observable} action$ - A stream of Redux actions.
* @returns {Observable} - A stream of Redux actions to be dispatched.
*/
export const getBrochureDownloadUrlEpic = (action$) =>
  action$.pipe(
    ofType(brochureActions.fetchBrochureDownloadUrl.type),
    filter((action) => action.payload.parameters.type === 'getBrochureDownloadUrl'),
    switchMap((action) =>
      from(getBrochureDownloadUrl(action.payload)).pipe(
        switchMap((response) => from(response.text())),
        switchMap((url) => of(brochureActions.fetchBrochureDownloadUrlSuccess(url))),
          catchError((error) => of(brochureActions.fetchBrochureDownloadUrlFailure(error)))
      )
    )
  );

